import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, X, FileText, CheckCircle2, Calendar, AlertTriangle, ArrowLeft, Mail, BadgeCheck } from "lucide-react";
import { AdminVerifiedBadge } from "@/components/AdminVerifiedBadge";
import { api, formatApiError, fileUrl } from "@/lib/api";

const DOC_FIELDS = [
  { key: "business_license", label: "Business License" },
  { key: "tourism_certificate", label: "Official Tourism Certificate" },
  { key: "government_id", label: "Government ID" },
];

function isImage(url = "") { return /\.(png|jpe?g|webp|gif|heic|heif)(\?|$)/i.test(url); }

export default function AdminHostVerify() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [host, setHost] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/hosts/${id}`).then(({ data }) => setHost(data)).catch(() => setHost(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const review = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/hosts/${id}/${action}`);
      toast.success(action === "approve" ? "Host verified & approved — provider notified" : "Host application rejected");
      navigate("/admin?tab=hosts");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  const forceApprove = async () => {
    if (!window.confirm("Force Approve this provider now? They'll be marked Active & Admin Verified even without uploaded documents.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/providers/${id}/force-approve`);
      toast.success("Force-approved — provider is now Active & Admin Verified");
      navigate("/admin?tab=providers");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  if (host === false) return <div className="min-h-screen bg-[#060810] flex items-center justify-center text-slate-400">Host not found.</div>;
  if (!host) return <div className="min-h-screen bg-[#060810] flex items-center justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>;

  const docs = host.verification_documents || {};
  const status = host.host_status || "pending";
  const expiry = docs.tourism_certificate_expiry;
  const expired = expiry && new Date(expiry) < new Date();

  return (
    <div className="min-h-screen bg-[#060810]" data-testid="admin-host-verify">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button onClick={() => navigate("/admin?tab=hosts")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 mb-6"><ArrowLeft className="w-4 h-4" /> Back to Approvals</button>

        <div className="glass neon-border rounded-2xl p-6 sm:p-8 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
              {host.avatar_url ? <img src={fileUrl(host.avatar_url)} alt="" className="w-full h-full object-cover" /> : <span className="text-white font-heading font-extrabold text-2xl">{host.name?.[0]?.toUpperCase()}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-mono-caption uppercase tracking-[0.3em] text-cyan-400 mb-1">Provider verification</p>
              <h1 className="font-heading font-extrabold text-white text-2xl">{host.name}</h1>
              {host.admin_verified && <div className="mt-1"><AdminVerifiedBadge /></div>}
              <p className="text-sm text-slate-400 flex items-center gap-1.5 mt-0.5"><Mail className="w-3.5 h-3.5" /> {host.email}</p>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${status === "approved" ? "text-emerald-400 border-emerald-400/30" : status === "rejected" ? "text-[#FF5E7E] border-[#FF5E7E]/30" : "text-amber-400 border-amber-400/30"}`}>{status}</span>
              <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${host.email_verified ? "text-cyan-400 border-cyan-400/30" : "text-amber-400 border-amber-400/30"}`}>{host.email_verified ? "Email verified" : "Email pending"}</span>
            </div>
          </div>
          {host.bio && <p className="text-slate-300 text-sm mt-4 leading-relaxed border-t border-[#1D2640] pt-4">{host.bio}</p>}
        </div>

        <p className="font-heading font-bold text-white text-lg flex items-center gap-2 mb-3"><FileText className="w-4.5 h-4.5 text-orange-400" /> Uploaded documents</p>
        <div className="grid sm:grid-cols-3 gap-4 mb-4" data-testid="host-documents">
          {DOC_FIELDS.map((d) => {
            const url = docs[d.key];
            return (
              <div key={d.key} className="glass rounded-xl overflow-hidden" data-testid={`doc-${d.key}`}>
                <div className="h-36 bg-[#0B0E1B] flex items-center justify-center overflow-hidden">
                  {url ? (
                    isImage(url)
                      ? <img src={fileUrl(url)} alt={d.label} className="w-full h-full object-cover" />
                      : <FileText className="w-10 h-10 text-cyan-400" />
                  ) : (
                    <div className="text-center text-slate-600"><AlertTriangle className="w-6 h-6 mx-auto mb-1" /><p className="text-xs">Not uploaded</p></div>
                  )}
                </div>
                <div className="p-3 flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-white leading-tight">{d.label}</p>
                  {url && <a href={fileUrl(url)} target="_blank" rel="noreferrer" data-testid={`open-doc-${d.key}`} className="text-[11px] px-2.5 py-1 rounded-lg glass text-cyan-300 hover:text-white shrink-0">Open</a>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="glass rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="tourism-expiry-row">
          <Calendar className="w-5 h-5 text-fuchsia-400 shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-white font-semibold">Tourism Certificate expiry</p>
            <p className="text-xs text-slate-400">Track when this host needs to renew their certificate.</p>
          </div>
          {expiry ? (
            <span className={`text-sm font-semibold px-3 py-1.5 rounded-lg ${expired ? "bg-[#FF5E7E]/15 text-[#FF5E7E]" : "bg-emerald-500/15 text-emerald-400"}`} data-testid="expiry-value">
              {new Date(expiry).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}{expired ? " · EXPIRED" : ""}
            </span>
          ) : <span className="text-sm text-slate-500" data-testid="expiry-value">Not provided</span>}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button data-testid="verify-approve-btn" onClick={() => review("approve")} disabled={busy || status === "approved"}
            className="flex-1 btn-neon rounded-xl px-6 py-4 text-base font-bold flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />} Verify & Approve
          </button>
          <button data-testid="reject-host-btn" onClick={() => review("reject")} disabled={busy || status === "rejected"}
            className="sm:w-48 rounded-xl px-6 py-4 text-base font-bold flex items-center justify-center gap-2 bg-[#FF5E7E]/10 text-[#FF5E7E] border border-[#FF5E7E]/30 hover:bg-[#FF5E7E]/25 transition-colors disabled:opacity-50">
            <X className="w-5 h-5" /> Reject
          </button>
        </div>
        <button data-testid="force-approve-btn" onClick={forceApprove} disabled={busy || host.admin_verified}
          className="mt-3 w-full rounded-xl px-6 py-3.5 text-sm font-bold flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:opacity-90 transition-opacity disabled:opacity-50">
          <BadgeCheck className="w-5 h-5" /> {host.admin_verified ? "Already Admin Verified" : "Force Approve (skip documents)"}
        </button>
        <p className="text-xs text-slate-500 text-center mt-2">Force Approve marks this provider Active &amp; Admin Verified instantly, even without uploaded documents.</p>
      </div>
    </div>
  );
}
